/* eslint-disable complexity */
import React, { DragEvent, useEffect, useState } from 'react';
import clsx from 'clsx';
import { FormattedMessage, useIntl } from 'react-intl';

import { Tab as CarbonTab, TabList, TabPanel, Tabs as CarbonTabs } from 'carbon-react/lib/components/tabs/__next__';

import { AvailableChartTypes } from '../available-chart-types/available-chart-types';
import { CARD_TYPES, WIDGET_TYPES } from '../../../types';
import { ChartThemeCustomization } from '../chart-theme-customization';
import { DatasetsSelector } from './datasets-selector';
import { DataSelector } from './data-selector';
import { PeriodSelector } from './period-selector';
import { TableThemeCustomization } from '../table-theme-customization';
import { TableColumnsSelector } from './table-columns-selector';
import { useLatestComparisonQueries } from './useLatestComparisonQueries';
import { useLatestWidgetDataQueries } from './useLatestWidgetDataQueries';
import { useReportBuilderContext } from '../../../report-builder.context';
import { widgetHasTwoAxes } from '../../../utils-data';

import styles from './builder-panel.scss';

type TabType = 'data-1' | 'data-2' | 'columns' | 'branding';

interface BuilderPanelProps {
  onItemDragStart: (
    e: DragEvent<HTMLDivElement | HTMLSpanElement>,
    type: CARD_TYPES,
    data?: Record<string, unknown>
  ) => void;
}

export const BuilderPanel = ({ onItemDragStart }: BuilderPanelProps) => {
  const intl = useIntl();
  const { setWidgetType, getWidgetType, currentCard, currentCardId, isTemplate } = useReportBuilderContext();

  const [selectedTab, setSelectedTab] = useState<TabType>('data-1');
  const widgetType = currentCardId ? getWidgetType() : undefined;

  useLatestComparisonQueries();
  useLatestWidgetDataQueries();

  const onWidgetTypeClick = (e: React.MouseEvent, item: WIDGET_TYPES) => {
    if (currentCardId) {
      setWidgetType(item);
    }
  };

  const onWidgetTypeDragStart = (e: React.DragEvent<HTMLSpanElement>, item: WIDGET_TYPES) => {
    onItemDragStart(e, CARD_TYPES.WIDGET, {
      widgetType: item,
      widgetTitle: '',
      widgetSubTitle: ''
    });
  };

  // resetting selected tab when selected card changes or cart type changes
  useEffect(() => {
    setSelectedTab('data-1');
  }, [currentCardId, widgetType]);

  const getPrimaryDataAxisName = () => {
    if (widgetType === WIDGET_TYPES.PIE || widgetType === WIDGET_TYPES.DONUT || widgetType === WIDGET_TYPES.DATA_CARD) {
      return intl.formatMessage({ id: 'reporting.values', defaultMessage: 'Values' });
    }
    if (widgetType === WIDGET_TYPES.TABLE) {
      return intl.formatMessage({ id: 'reporting.rows', defaultMessage: 'Rows' });
    }
    return intl.formatMessage({ id: 'reporting.axis_1', defaultMessage: 'Axis 1' });
  };

  return (
    <div className={clsx(styles.builderContainer, isTemplate && styles.templateColor)}>
      <div className={clsx(styles.topSection)}>
        <h3>
          <FormattedMessage id="reporting.chart_table_or_scoreboard" defaultMessage="Chart, table or scoreboard" />
        </h3>
        <AvailableChartTypes
          currentWidgetType={widgetType}
          onClick={onWidgetTypeClick}
          onDragStart={onWidgetTypeDragStart}
        />
        {widgetType && currentCard?.i === currentCardId && (
          <>
            <DatasetsSelector key={`datasets-${currentCardId}`} />
            <PeriodSelector key={`periods-${currentCardId}`} />
          </>
        )}
      </div>
      {widgetType && currentCard?.i === currentCardId && (
        <div className={clsx(styles.tabsContainerCarbon)}>
          <CarbonTabs selectedTabId={selectedTab}>
            <TabList
              ariaLabel={intl.formatMessage({
                id: 'common.customization_tabs.aria_label',
                defaultMessage: 'Customization Tabs'
              })}
              onTabChange={tab => setSelectedTab(tab as TabType)}
            >
              <CarbonTab id="data-1" controls="data-1-panel" label={getPrimaryDataAxisName()} />
              {widgetHasTwoAxes(widgetType) && (
                <CarbonTab
                  id="data-2"
                  controls="data-2-panel"
                  label={intl.formatMessage({ id: 'reporting.axis_2', defaultMessage: 'Axis 2' })}
                />
              )}
              {widgetType === WIDGET_TYPES.TABLE && (
                <CarbonTab
                  id="columns"
                  controls="columns-panel"
                  label={intl.formatMessage({ id: 'reporting.columns', defaultMessage: 'Columns' })}
                />
              )}
              {widgetType !== WIDGET_TYPES.DATA_CARD && (
                <CarbonTab
                  id="branding"
                  controls="branding-panel"
                  label={intl.formatMessage({ id: 'reporting.branding', defaultMessage: 'Branding' })}
                />
              )}
            </TabList>
            <TabPanel id="data-1-panel" tabId="data-1">
              <DataSelector key={`data-selector-primary-${currentCardId}`} axisName={getPrimaryDataAxisName()} />
            </TabPanel>
            {widgetHasTwoAxes(widgetType) && (
              <TabPanel id="data-2-panel" tabId="data-2">
                <DataSelector
                  key={`data-selector-secondary-${currentCardId}`}
                  axisName={intl.formatMessage({ id: 'reporting.axis_2', defaultMessage: 'Axis 2' })}
                  isSecondary
                />
              </TabPanel>
            )}
            {widgetType === WIDGET_TYPES.TABLE && (
              <TabPanel id="columns-panel" tabId="columns">
                {selectedTab === 'columns' && (
                  <TableColumnsSelector key={`table-theme-customization-${currentCardId}`} />
                )}
              </TabPanel>
            )}
            {widgetType !== WIDGET_TYPES.DATA_CARD && (
              <TabPanel id="branding-panel" tabId="branding">
                {selectedTab === 'branding' && (
                  <>
                    {widgetType === WIDGET_TYPES.TABLE ? (
                      <TableThemeCustomization key={`table-theme-customization-${currentCardId}`} />
                    ) : (
                      <ChartThemeCustomization key={`chart-theme-customization-${currentCardId}`} />
                    )}
                  </>
                )}
              </TabPanel>
            )}
          </CarbonTabs>
        </div>
      )}
    </div>
  );
};
